/// <amd-module name="Feed/Legacy/Common/NavigationHandler" />
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";

import { HubActionStrings } from "Feed/Common/Constants/Constants";
import * as PackageResources from "Feed/Common/Resources";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { getFullyQualifiedFeedName } from "Feed/Legacy/Helpers/FeedNameResolver";
import { IHubState } from "Feed/Legacy/Types/IHubState";
import { toHubState, toNavState } from "Feed/Legacy/Utils/Hub";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class NavigationHandler {
    private static getHistoryService(pageContext: IVssPageContext): IVssHistoryService {
        return pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }

    /**
     * Update the uri to reflect changes in case feed name changed.
     */
    public static updateFeedNameInUrlIfChanged(pageContext: IVssPageContext, feed: Feed): void {
        const currentState = this.getHistoryService(pageContext).getState() as IHubState;
        if (currentState.feed === feed.name) {
            return;
        }
        this.updateUrlWithFeedName(pageContext, feed);
    }

    public static updateFeedHistoryOnLanding(pageContext: IVssPageContext, feed: Feed): void {
        const navState = this.getHistoryService(pageContext).getState();
        const currentState: IHubState = toHubState(navState);

        // for create new feed
        if (currentState.action === HubActionStrings.CreateFeed) {
            this.updateUrlForCreateNewFeed(pageContext);
            return;
        }

        // action must be feed or settings
        const hubState = this.getHubState(feed);
        hubState.action = currentState.action || HubActionStrings.ViewFeed;
        hubState.view = currentState.view;

        this.updateHistoryEntry(
            pageContext,
            hubState, // hubState.action could be either feed or settings
            true, // replaceHistoryEntry
            null // derive title from state or use default title
        );
    }

    // updates the url to show the filtered view (if applicable)
    public static updateUrlWithFilterValues(pageContext: IVssPageContext, feed: Feed): void {
        const hubState = this.getHubState(feed);
        hubState.action = HubActionStrings.ViewFeed;

        this.updateHistoryEntry(
            pageContext,
            hubState,
            false, // replaceHistoryEntry
            null // derive title from state or use default title
        );
    }

    public static updateUrlWithFeedName(pageContext: IVssPageContext, feed: Feed): void {
        const hubState = this.getHubState(feed);
        hubState.action = HubActionStrings.Settings;

        this.updateHistoryEntry(
            pageContext,
            hubState,
            true, // replaceHistoryEntry
            null // derive title from state or use default title
        );
    }

    public static updateUrlForCreateNewFeed(pageContext: IVssPageContext): void {
        this.updateHistoryEntry(
            pageContext,
            { action: HubActionStrings.CreateFeed } as IHubState,
            true, // replaceHistoryEntry
            PackageResources.CreateFeed_PageTitle // page title
        ); // suppressNavigate - just updating url
    }

    public static renderFeed(pageContext: IVssPageContext, feed: Feed): void {
        const hubState = this.getHubState(feed);
        hubState.action = HubActionStrings.ViewFeed;

        const title = feed != null ? feed.name : PackageResources.HubTitle;
        this.updateHistoryEntry(
            pageContext,
            hubState,
            true, // replaceHistoryPoint
            title
        ); // suppressNavigate

        this.navigateToHub(HubActionStrings.ViewFeed);
    }

    private static navigateToHub(action: string) {
        // until stores are merged, need to trigger this action in next cycle
        // else "action cannot be invoked within am action" will be thrown
        new TimerManagement().setImmediate(() => Actions.NavigateToHub.invoke({ action }));
    }

    private static getHubState(feed: Feed): IHubState {
        const state: IHubState = {
            feed: getFullyQualifiedFeedName(feed)
        } as IHubState;

        return state;
    }

    private static updateHistoryEntry(
        pageContext: IVssPageContext,
        hubState: IHubState,
        replaceHistoryEntry?: boolean,
        windowTitle?: string
    ): void {
        if (replaceHistoryEntry) {
            const navState: { [key: string]: string } = toNavState(pageContext, hubState);
            this.getHistoryService(pageContext).replaceState(navState, null, null, hubState);
        } else {
            NavigationHandler.pushHistoryEntry(pageContext, hubState);
        }

        windowTitle = windowTitle || (hubState != null && hubState.feed) || PackageResources.HubTitle;
        this.setWindowTitle(windowTitle);
    }

    // don't add new usages of this function, for nav back-compat only
    public static pushHistoryEntry(pageContext: IVssPageContext, hubState: IHubState): void {
        const navState: { [key: string]: string } = toNavState(pageContext, hubState);
        // this is a workaround so that NavigationSaga and NavigationHandler don't both push state
        const historyService = this.getHistoryService(pageContext);
        const newUrl = historyService.generateUrl(navState, StateMergeOptions.none);
        const currentUrl = historyService.generateUrl({}, StateMergeOptions.currentState);
        if (newUrl !== currentUrl) {
            this.getHistoryService(pageContext).pushState(navState, null, null, hubState);
        }
    }

    private static setWindowTitle(title: string): void {
        document.title = title;
    }
}
